/**
 * MODULE: domain/pages/frontend/utils/commentMentionUtils.ts
 * PURPOSE: Parse and build @mention tokens in page comment text
 */

import type { WorkspaceMember } from '@domain/workspace';
import { formatWorkspaceMemberLabel } from './pageShareMemberUtils';

/** Token format persisted in comment content: @[userId:Display Name] */
export const MENTION_TOKEN_PATTERN =
  /@\[([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}):([^\]]+)\]/gi;

export type CommentContentPart =
  | { type: 'text'; value: string }
  | { type: 'mention'; userId: string; label: string };

export function buildMentionToken(userId: string, label: string): string {
  return `@[${userId}:${label}]`;
}

export function buildMentionTokenForMember(member: WorkspaceMember): string {
  return buildMentionToken(member.user_id, formatWorkspaceMemberLabel(member));
}

export function extractMentionedUserIds(content: string): string[] {
  const ids: string[] = [];
  const seen = new Set<string>();
  for (const match of content.matchAll(MENTION_TOKEN_PATTERN)) {
    const userId = match[1];
    if (!userId || seen.has(userId)) {
      continue;
    }
    seen.add(userId);
    ids.push(userId);
  }
  return ids;
}

export function parseCommentContent(content: string): CommentContentPart[] {
  const parts: CommentContentPart[] = [];
  let lastIndex = 0;
  for (const match of content.matchAll(MENTION_TOKEN_PATTERN)) {
    const start = match.index ?? 0;
    if (start > lastIndex) {
      parts.push({ type: 'text', value: content.slice(lastIndex, start) });
    }
    parts.push({
      type: 'mention',
      userId: match[1] ?? '',
      label: match[2] ?? '',
    });
    lastIndex = start + match[0].length;
  }
  if (lastIndex < content.length) {
    parts.push({ type: 'text', value: content.slice(lastIndex) });
  }
  return parts.length > 0 ? parts : [{ type: 'text', value: content }];
}

export function getActiveMentionQuery(
  text: string,
  caretIndex: number
): { query: string; atIndex: number } | null {
  const beforeCaret = text.slice(0, caretIndex);
  const atIndex = beforeCaret.lastIndexOf('@');
  if (atIndex < 0) {
    return null;
  }
  const query = beforeCaret.slice(atIndex + 1);
  if (query.includes(' ') || query.includes('[')) {
    return null;
  }
  return { query, atIndex };
}

export function filterMembersForMentionQuery(
  members: WorkspaceMember[],
  query: string,
  options?: { excludeUserId?: string }
): WorkspaceMember[] {
  const normalized = query.trim().toLowerCase();
  return members
    .filter((member) => member.user_id !== options?.excludeUserId)
    .filter((member) => {
      if (!normalized) {
        return true;
      }
      const label = formatWorkspaceMemberLabel(member).toLowerCase();
      const email = member.user?.email?.toLowerCase() ?? '';
      const name = member.user?.name?.toLowerCase() ?? '';
      return (
        label.includes(normalized) ||
        email.includes(normalized) ||
        name.includes(normalized)
      );
    })
    .slice(0, 8);
}

export function insertMentionAtQuery(
  text: string,
  caretIndex: number,
  mentionToken: string
): { nextText: string; nextCaret: number } {
  const active = getActiveMentionQuery(text, caretIndex);
  if (!active) {
    const nextText = `${text.slice(0, caretIndex)}${mentionToken} ${text.slice(caretIndex)}`;
    const nextCaret = caretIndex + mentionToken.length + 1;
    return { nextText, nextCaret };
  }
  const nextText =
    text.slice(0, active.atIndex) + mentionToken + ' ' + text.slice(caretIndex);
  const nextCaret = active.atIndex + mentionToken.length + 1;
  return { nextText, nextCaret };
}
